import api from './client'

// Node member info returned by /api/cluster/status and /api/dashboard
export interface ClusterDisk {
  total?: number
  available?: number
  used?: number
  percent?: number
}

export interface ClusterMember {
  id: string
  name?: string
  addr?: string
  grpc_port?: number
  api_port?: number
  status?: string
  online?: boolean
  mode?: string
  region?: string
  tags?: Record<string, string>
  /** 磁盘信息 (后端 memberMap.disk) */
  disk?: ClusterDisk
  /** 任务负载 (gossip 心跳上报) */
  load?: number
  /** 是否本机节点 */
  is_local?: boolean
  /** devices.json 分组覆盖 */
  group?: string
  data_dir?: string
  /**
   * 成员证书指纹 (v3.19.2)。
   * `id` 是 config node.id (集群身份命名空间), `device_id` 才是操作者
   * 在「添加设备」里粘贴的那个 42 位 ID — 配对状态轮询必须用后者匹配。
   */
  device_id?: string
  /** 节点间 RTT (v3.19.46, 本机视角最近一次 gRPC Ping 往返, ms)。null = 不可达/未测 */
  ping_ms?: number | null
  /** EMA 平滑平均 (α=0.3) */
  ping_avg?: number | null
}

export interface ClusterStatus {
  // /api/cluster/status (public)
  cluster_enabled: boolean
  device_id?: string
  node_id?: string
  is_leader?: boolean
  leader_id?: string
  version?: string
  nodes?: ClusterMember[]
  members?: ClusterMember[]
  total_nodes?: number
  online_nodes?: number
  mode?: string
  data_dir?: string
  disk_total?: number
  disk_used?: number
  disk_free?: number
  task_count?: number
  // Optional auth_token — never expose by default; UI uses it via dedicated endpoint if needed.
}

// --- v3.18.2 BT 互联状态 ---

export interface BTPeerStatus {
  ip: string
  port: number
  seeder: boolean
  expiry: string
}

export interface BTInfoHashStatus {
  info_hash: string
  peers: BTPeerStatus[]
}

export interface BTSeedInfo {
  info_hash: string
  name: string
  peers: number
  uploaded: number
  ratio: number
  active: boolean
}

// v3.18.4: 集群节点一览 — tracker 内置的节点名单 (地址/在线/是否已通告)
export interface BTNodeInfo {
  id: string
  name: string
  addr: string
  bt_port: number
  status: string
  is_local: boolean
  announced: boolean
}

export interface BTStatus {
  tracker: BTInfoHashStatus[]
  tracker_peers: number
  seeds: BTSeedInfo[]
  dht: { enabled: boolean }
  bt_port: number
  nodes?: BTNodeInfo[]
}

export async function getBTStatus(): Promise<BTStatus> {
  const empty: BTStatus = { tracker: [], tracker_peers: 0, seeds: [], dht: { enabled: false }, bt_port: 0 }
  try {
    // v3.18.4 fix: /api/bt/status 走 okJSON 包装 ({data: {...}}), api.get 不
    // 解包 — 此前直接把整个包装对象当 BTStatus 用, 卡片从 v3.18.2 起一直
    // 渲染空态 (0 peer / 0 做种 / 无节点一览)。
    const res = await api.get<{ data?: BTStatus }>('/api/bt/status')
    return res?.data ?? empty
  } catch {
    return empty
  }
}

export async function getClusterStatus(): Promise<ClusterStatus> {
  return api.get<ClusterStatus>('/api/cluster/status')
}

// /api/cluster/device-id — short Syncthing-style device ID (text/plain)
export async function getDeviceId(): Promise<string> {
  return api.get<string>('/api/cluster/device-id')
}

// 邀请信息: 设备 ID + 本机可达地址。
// v3.19.2 补回地址字段 —— v3.13.48 删掉它们的前提是"地址靠 gossip 自动
// 发现", 而该前提只在同网段成立, 跨公网时对方拿到 ID 仍然无址可拨。
export interface ClusterInvite {
  device_id: string
  node_name: string
  /** 本节点可达的 gRPC 地址 ("host:50051") */
  grpc_addrs?: string[]
  /** 本节点可达的 discovery 地址 ("host:50052") */
  discovery_addrs?: string[]
  /** 是否解析得出公网可达地址。false 时跨公网配对必然失败, 应提示配 public_grpc_addr */
  has_public_addr?: boolean
}

export async function getClusterInvite(): Promise<ClusterInvite> {
  return api.get<ClusterInvite>('/api/cluster/invite')
}

// v3.13.48: 待确认配对队列 — 未知/被删节点拨入时由后端入队, 操作者接受/拒绝。
export interface PairingRequest {
  fingerprint: string
  node_id: string
  name?: string
  addr?: string
  at?: string
}

export async function getPairingPending(): Promise<{ requests: PairingRequest[] }> {
  return api.get<{ requests: PairingRequest[] }>('/api/cluster/pairing/pending')
}

// v3.19.27: 本机"添加设备"提交的终态 — rejected → toast + 30s 按钮锁;
// failed → 超时提示; state 为空 = 没有已记录的提交。
export interface PairingOutcome {
  state: '' | 'rejected' | 'failed' | 'connected'
  at?: string
}

export async function getPairingOutcome(deviceId: string): Promise<PairingOutcome> {
  return api.get<PairingOutcome>(`/api/cluster/pairing/outcome?device_id=${encodeURIComponent(deviceId)}`)
}

/** 从来源地址 "ip:port" 取 host 部分 (兼容 IPv6 "[::1]:port"); 无地址返回空。 */
export function peerHost(addr?: string): string {
  if (!addr) return ''
  const trimmed = addr.trim()
  if (trimmed.startsWith('[')) {
    const end = trimmed.indexOf(']')
    return end > 0 ? trimmed.slice(1, end) : trimmed
  }
  const colon = trimmed.lastIndexOf(':')
  return colon > 0 ? trimmed.slice(0, colon) : trimmed
}

export async function acceptPairing(fingerprint: string): Promise<{ ok: boolean; node_id: string }> {
  return api.post<{ ok: boolean; node_id: string }>('/api/cluster/pairing/accept', { fingerprint })
}

export async function rejectPairing(fingerprint: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/api/cluster/pairing/reject', { fingerprint })
}

export interface AllowedDevice {
  device_id: string
  label?: string
  added_at: string
  trusted: boolean
  /** 配对握手成功后学习到的对端 node.id */
  node_id?: string
}

export async function listAllowedDevices(): Promise<{ devices: AllowedDevice[] }> {
  return api.get<{ devices: AllowedDevice[] }>('/api/cluster/allowed-devices')
}

export interface AddDeviceRequest {
  device_id: string
  label?: string
  /** 对端 discovery 地址 ("host:50052") → 写入 join_peers 并加入 gossip */
  addrs?: string[]
  /** 对端 gRPC 地址 ("host:50051") → 立即发起配对握手 */
  grpc_addrs?: string[]
}

export interface AddDeviceResponse {
  ok: boolean
  device_id: string
  label?: string
  /** 后端已进入后台拨号循环, 正在等对方管理员接受 */
  pending?: boolean
  message?: string
}

export async function addDevice(payload: AddDeviceRequest): Promise<AddDeviceResponse> {
  return api.post<AddDeviceResponse>('/api/cluster/add-device', payload)
}

// /api/cluster/remove-device POST {node_id} — drop a node from membership.
export interface RemoveDeviceResponse {
  ok: boolean
  node_id: string
  message: string
}

export async function removeDevice(nodeId: string): Promise<RemoveDeviceResponse> {
  return api.post<RemoveDeviceResponse>('/api/cluster/remove-device', { node_id: nodeId })
}

// /api/cluster/node-settings POST {node_id, mode} — both | upload_only | download_only
export type NodeMode = 'both' | 'upload_only' | 'download_only'

export interface NodeSettingsRequest {
  node_id: string
  mode: NodeMode
}

export interface NodeSettingsResponse {
  ok: boolean
  node_id: string
  mode: NodeMode
  message: string
}

export async function setNodeSettings(payload: NodeSettingsRequest): Promise<NodeSettingsResponse> {
  return api.post<NodeSettingsResponse>('/api/cluster/node-settings', payload)
}

// /api/cluster/device-meta GET — read local node metadata (name, group, mode, data_dir)
export interface DeviceMeta {
  id?: string
  name?: string
  group?: string
  mode?: NodeMode
  data_dir?: string
  tags?: string[]
}

export async function getDeviceMeta(): Promise<DeviceMeta> {
  return api.get<DeviceMeta>('/api/cluster/device-meta')
}

export async function updateDeviceMeta(meta: DeviceMeta): Promise<DeviceMeta> {
  return api.post<DeviceMeta>('/api/cluster/device-meta', meta)
}
